import React, { useContext, useRef, useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ImpressionProvider } from '@thd-olt-component-react/impression';

import {
  params,
  extend,
  shape as dsShape,
  string as dsString,
  QueryProvider,
  number as dsNumber,
  arrayOf as dsArrayOf,
  bool as dsBool,
  typename,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';

import {
  getSchema,
  getSchemaId,
} from '../../utils/helpers';
import { triggerNewRelic, triggerNewRelicTimeLog } from '../../utils/newRelicHelpers';
import { buildRevJetBanner } from '../../utils/revjet';
import useSponsoredBannerApi from '../../hooks/useSponsoredBannerApi';
import { normalizeMediaSlotData } from '../../utils/normalizeMediaSlotData';
import { DefaultBanner } from '../DefaultBanner/DefaultBannerComponent';
import {
  SponsoredUniversalBannerCreative
} from '../SponsoredUniversalBannerCreative/SponsoredUniversalBannerCreative';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import { newRelicConstants } from '../../constants';
import { adServerClients, SUPPRESSED_NVALUES } from '../../constants/constants';
import { shouldSuppressBanner, suppressBannerReason } from '../../utils/suppressionHelper';

const {
  string, shape, bool, object, number
} = proptypes;

const SponsoredUniversalBannerComponent = ({
  contentfulDefaultBanner: contentfulDefaultBannerProp,
  pageContext,
  schema,
  bannerType,
  renderDefaultBanner,
  renderHarmony,
  nValue,
  shouldPreload,
  slotContentId,
  mediaCollectionIndex,
}) => {
  const experienceContext = useContext(ExperienceContext);
  let { guestUserID, mcvisID } = useThdCustomer();

  const suppressedNValues = useConfigService(SUPPRESSED_NVALUES);
  const isLeaderboardEnabled = useConfigService('fs-prop:isLeaderboardEnabled') || false;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (experienceContext?.mounted && !mounted) {
      setMounted(true);
    }
  }, [experienceContext?.mounted, mounted]);

  let matchProducts;
  if (renderHarmony) {
    mcvisID = '1234';
    guestUserID = '4321';
    matchProducts = [1.1];

  }

  const {
    data: mediaSlotData,
  } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotContentId },
    skip: !slotContentId,
  });

  const { data: normalizedMediaSlotData } = normalizeMediaSlotData({
    mediaSlotData,
    mediaCollectionIndex,
  });
  // When slotContentId is present, source contentfulDefaultBanner from slot bannerURL and clickthruURL
  const contentfulDefaultBanner = slotContentId && normalizedMediaSlotData?.sponsored?.banner
    ? {
      clickthruUrl: null,
      imgDesktop: null,
      imgMobile: null,
      specialDefaultBanner: {
        imgDesktop: normalizedMediaSlotData.sponsored.banner.bannerURL,
        imgMobile: normalizedMediaSlotData.sponsored.banner.bannerURLMobile,
        clickthruUrl: normalizedMediaSlotData.sponsored.banner.clickthruURL,
        useSpecialBanner: normalizedMediaSlotData.sponsored.banner.useSpecialBanner,
      },
    }
    : contentfulDefaultBannerProp;
  const bannerSchema = getSchema({ schema, pageContext });
  const schemaId = getSchemaId(bannerSchema, bannerType);
  const shouldSuppress = shouldSuppressBanner({
    bannerType, experienceContext, pageContext, suppressedNValues
  });
  const bannerSuppressionReason = suppressBannerReason({ experienceContext, pageContext, suppressedNValues });
  const [hasTriggeredNewRelicForSuppression, setHasTriggeredNewRelicForSuppression] = useState(false);
  const newRelicBannerType = newRelicConstants[`${bannerType.toUpperCase()}_BANNER`];
  const bannerReceivedRef = useRef(null);
  const apiRequestTimestamp = new Date().getTime();
  const noAdReturnedTriggeredRef = useRef(false);
  const anchorSku = pageContext?.data?.itemId;
  const { isBrowse, isSearch } = pageContext;

  useEffect(() => {
    if (shouldSuppress && !hasTriggeredNewRelicForSuppression) {
      triggerNewRelic(newRelicBannerType, bannerSuppressionReason);
      setHasTriggeredNewRelicForSuppression(true);
    }
  }, [shouldSuppress, hasTriggeredNewRelicForSuppression]);

  let { data, loading, error } = useSponsoredBannerApi({
    bannerType,
    guestUserID,
    experienceContext,
    pageContext,
    schema: bannerSchema,
    mcvisID,
    shouldSuppress,
    isLeaderboardEnabled,
    matchProducts,
    anchorSku,
    mounted,
    nValue
  });

  let apiResponseTimestamp;
  const showBanner = !!(!loading && data?.sponsored?.banner);

  if (showBanner && bannerReceivedRef.current !== false) {
    apiResponseTimestamp = new Date().getTime();
    bannerReceivedRef.current = !bannerReceivedRef.current;
  }

  let adWasNotReturned = !loading && !error && data?.data?.sponsored?.banner === null && !noAdReturnedTriggeredRef.current;
  if (error) {
    triggerNewRelic(newRelicBannerType, 'BANNER-API-FAILURE');
  }
  if (adWasNotReturned) {
    triggerNewRelic(newRelicBannerType, 'BANNER-API-204');
    noAdReturnedTriggeredRef.current = true;
  }

  if (bannerReceivedRef.current) {
    if (bannerType === 'standard') {
      initImpressionWatchers(['UNIVERSAL_STANDARD_BANNER']);
    } else {
      initImpressionWatchers();
    }
    triggerNewRelic(newRelicBannerType, 'BANNER-API-200');
    const sponsoredData = data.sponsored;
    const requestConfig = {
      schemaId,
      experienceContext,
      pageContext,
      bannerType,
      mcvisID,
    };
    requestConfig.startTime = apiRequestTimestamp;
    buildRevJetBanner({
      data: sponsoredData,
      requestConfig,
      useKevelInnervate: false
    });
    // SEND TIMESTAMPS TO CALCULATE API RESPONSE TIME
    triggerNewRelicTimeLog(newRelicConstants[`${bannerType.toUpperCase()}_BANNER_API_RESPONSE`], apiRequestTimestamp, apiResponseTimestamp, adServerClients.KEVEL);
    // SEND TIME FROM BANNER INITIATED TO BANNER WINNER RECEIVED
    triggerNewRelicTimeLog(newRelicConstants[`${bannerType.toUpperCase()}_BANNER_AD_INIT_TO_WINNER`], apiRequestTimestamp, apiResponseTimestamp, adServerClients.KEVEL);
  }

  return (
    <div
      data-component={`Sponsored${bannerType}Banner`}
      className={`sponsored_${bannerType}_banner_container sui-overflow-hidden`}
    >

      {renderDefaultBanner && showBanner && (
        <Carousel
          containerId="sponsored-standard-banner"
          type="peekless"
          layout="full-bleed"
          slidesPerView={1}
          slidesPerGroup={2}
          disableMargin
          disableShadow
          SwiperProps={
            { autoplay: { delay: 500, stopOnLastSlide: true, } }
          }
        >
          <ImpressionProvider
            data={{
              id: '',
              name: 'SponsoredUniversalBanner',
              component: 'SponsoredUniversalBanner',
              position: 1,
              type: 'sponsoredcontent',
            }}
          >

            <DefaultBanner
              {...{
                experienceContext,
                contentfulDefaultBanner,
                schema,
                isBrowse,
                isSearch,
                shouldSuppress,
                shouldPreload
              }}
            />
          </ImpressionProvider>
          <ImpressionProvider
            data={{
              id: '',
              name: 'SponsoredUniversalBanner',
              component: 'SponsoredUniversalBanner',
              type: 'sponsoredcontent',
            }}
          >

            <div className="sui-w-full sui-h-auto">
              <SponsoredUniversalBannerCreative
                {...{ schemaId,
                  showBanner,
                  bannerType,
                  data }}
              />
            </div>

          </ImpressionProvider>

        </Carousel>
      )}

      {renderDefaultBanner && !showBanner && (
        <ImpressionProvider
          data={{
            id: '',
            name: 'SponsoredUniversalBanner',
            component: 'SponsoredUniversalBanner',
            position: 1,
            type: 'sponsoredcontent',
          }}
        >
          <DefaultBanner
            {...{
              experienceContext,
              contentfulDefaultBanner,
              schema: bannerSchema,
              isBrowse,
              isSearch,
              shouldSuppress,
              shouldPreload
            }}
          />

        </ImpressionProvider>

      )}

      {!renderDefaultBanner && showBanner && (
        <ImpressionProvider
          data={{
            id: '',
            name: 'SponsoredUniversalBanner',
            component: 'SponsoredUniversalBanner',
            type: 'sponsoredcontent',
          }}
        >
          <SponsoredUniversalBannerCreative
            {...{ schemaId,
              showBanner,
              bannerType,
              data }}
          />
        </ImpressionProvider>

      )}

    </div>
  );

};

const propTypes = {
  contentfulDefaultBanner: object,
  pageContext: shape({
    label: string
  }),
  schema: string,
  nValue: string,
  bannerType: string,
  renderDefaultBanner: bool,
  renderHarmony: bool,
  shouldPreload: bool,
  slotContentId: string,
  mediaCollectionIndex: number,
};

const defaultProps = {
  contentfulDefaultBanner: {},
  pageContext: {},
  schema: '',
  bannerType: '',
  renderDefaultBanner: false,
  renderHarmony: false,
  nValue: '',
  shouldPreload: true,
  slotContentId: '',
  mediaCollectionIndex: 0,
};

SponsoredUniversalBannerComponent.propTypes = propTypes;
SponsoredUniversalBannerComponent.defaultProps = defaultProps;
SponsoredUniversalBannerComponent.displayName = 'SponsoredUniversalBannerComponent';
SponsoredUniversalBannerComponent.dataModel = extend({
  sponsored: params({
    bannerType: dsString(),
    matchProducts: dsArrayOf(dsNumber({ float: true })),
    keyword: dsString(),
    marketingId: dsString().isRequired(),
    pageType: dsString().isRequired(),
    customerType: dsString(),
    category: dsString(),
    channel: dsString().isRequired(),
    customerIdentifier: dsString(),
    anchorSku: dsNumber({ float: true }),
    nValue: dsString(),
    forceAd: dsBool()
  }).shape({
    banner: dsShape({
      bannerURL: dsString(),
      clickthruURL: dsString(),
      onClickBeacon: dsString(),
      clickConversionBeacon: dsString(),
      viewConversionBeacon: dsString(),
      onViewBeacon: dsString(),
      slotId: dsString(),
      adRequestId: dsString(),
      campaignId: dsString(),
      placementId: dsString(),
      flightId: dsString(),
      innervateKey: dsString(),
      innervateTag: dsString()
    })
  }),
  slot: params({ id: dsString().isRequired() }).shape({
    id: dsString(),
    entryId: dsString(),
    mediaCollections: dsArrayOf(dsShape({
      entryId: dsString(),
      title: dsString(),
      subtitle: dsString(),
      theme: dsString(),
      description: dsString(),
      media: dsArrayOf(dsShape({
        entryId: dsString(),
        title: dsString(),
        subtitle: dsString(),
        schema: dsString(),
        isSponsored: dsBool(),
        description: dsString(),
        customProps: dsString(),
        sourceDesktop: dsArrayOf(dsShape({
          entryId: dsString(),
          title: dsString(),
          uri: dsString(),
          altText: dsString(),
          hyperlink: dsString(),
          dimensions: dsString(),
        })),
        sourceMobile: dsArrayOf(dsShape({
          entryId: dsString(),
          title: dsString(),
          uri: dsString(),
          altText: dsString(),
          hyperlink: dsString(),
          dimensions: dsString(),
        })),
      })),
    })),
    __typename: typename('newSlot'),
  }),
});

const SponsoredUniversalBanner = (props) => {

  const skipFn = ({ skip, queryName }) => {
    return skip;
  };
  return (
    <QueryProvider cacheKey="sponsored-universal-banner" skip={skipFn}>
      <SponsoredUniversalBannerComponent {...props} />
    </QueryProvider>
  );
};
SponsoredUniversalBanner.propTypes = propTypes;

SponsoredUniversalBanner.defaultProps = defaultProps;
SponsoredUniversalBanner.displayName = 'SponsoredUniversalBanner';
SponsoredUniversalBanner.dataModel = extend({}, SponsoredUniversalBannerComponent);
export { SponsoredUniversalBanner };
